import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import { detailsRefus } from './erreurs-api';
import type { VueDeclaration, VuePrevision } from './useEspaceFormateur';

// ---------------------------------------------------------------------------
// Espace documentaire et facturation du formateur (CDC §7.4). Les règles —
// pièces exigées selon le statut, péremption, refus de dépôt, de retrait, de
// transmission et de vérification — sont tenues par l'API : chaque ligne
// porte ce qui l'empêche, l'écran ne recalcule rien.
// ---------------------------------------------------------------------------

export type StatutFormateur = 'INDEPENDANT' | 'SALARIE';
export type TypePieceFormateur =
  | 'ATTESTATION_URSSAF'
  | 'KBIS_SIRENE'
  | 'RC_PRO'
  | 'CV_DIPLOMES'
  | 'RIB'
  | 'CONVENTION_SIGNEE';
export type EtatPieceFormateur = 'MANQUANTE' | 'A_VERIFIER' | 'CONFORME' | 'REFUSEE' | 'PERIMEE';
export type StatutDepot = 'ATTENTE' | 'VALIDE' | 'REFUSE';
export type StatutFacture =
  | 'TRANSMISE'
  | 'VERIFIEE'
  | 'A_CORRIGER'
  | 'VALIDEE'
  | 'TRANSMISE_DIRECTION'
  | 'PAYEE';
export type TypeFacture = 'CFA' | 'OF';

/** Une étape de la carte « Circuit de validation » (TF-401), calculée par l'API. */
export interface EtapeCircuit {
  etape: 'FORMATEUR' | 'VERIFICATION' | 'FACTURATION' | 'DIRECTION';
  libelle: string;
  detail: string;
  etat: 'FAIT' | 'EN_COURS' | 'A_CORRIGER' | 'A_VENIR';
  precision: string | null;
}

export interface LigneHistoriqueFacture {
  id: string;
  geste: string;
  libelle: string;
  le: string;
  auteur: string | null;
  motif: string | null;
}

export const LIBELLES_STATUT_FORMATEUR: Record<StatutFormateur, string> = {
  INDEPENDANT: 'Formateur indépendant',
  SALARIE: 'Formateur salarié',
};

export interface DepotFormateur {
  id: string;
  nomFichier: string;
  typeMime: string;
  deposeLe: string;
  delivreLe: string | null;
  valableJusquAu: string | null;
  finValidite: string | null;
  statut: StatutDepot;
  statutLibelle: string;
  statutTon: string;
  verifieLe: string | null;
  verifiePar: string | null;
  motifRefus: string | null;
  refusRetrait: string | null;
  refusVerification: string | null;
}

export interface PieceFormateur {
  type: TypePieceFormateur;
  libelle: string;
  detail: string;
  multiple: boolean;
  date: 'DELIVRANCE' | 'ECHEANCE' | null;
  exigee: boolean;
  etat: EtatPieceFormateur;
  etatLibelle: string;
  etatTon: string;
  precision: string | null;
  depots: DepotFormateur[];
}

export interface FactureFormateur {
  id: string;
  numero: string;
  siteCode: string;
  debut: string;
  fin: string;
  montant: number;
  heuresValidees: number;
  tauxHoraire: number | null;
  montantAttendu: number | null;
  siret: string;
  iban: string | null;
  bic: string | null;
  titulaireCompte: string | null;
  nomFichier: string;
  statut: StatutFacture;
  etatLibelle: string;
  etatTon: string;
  transmiseLe: string;
  verifieeLe: string | null;
  verifieePar: string | null;
  renvoyeeLe: string | null;
  renvoyeePar: string | null;
  motifRenvoi: string | null;
  valideeLe: string | null;
  valideePar: string | null;
  transmiseDirectionLe: string | null;
  payeeLe: string | null;
  referencePaiement: string | null;
  type: TypeFacture | null;
  typeLibelle: string | null;
  circuit: EtapeCircuit[];
  historique: LigneHistoriqueFacture[];
  fiches: { id: string; titre: string; date: string | null; heures: number }[];
  refusVerification: string | null;
  refusRenvoi: string | null;
  refusCorrection: string | null;
}

export interface ConventionFormateur {
  id: string;
  debut: string;
  fin: string;
  heuresPrevues: number | null;
  tauxHoraire: number | null;
  formations: string[];
  genereLe: string;
  generePar: string | null;
  nomFichier: string;
  exemplaire: { id: string; statut: StatutDepot; statutLibelle: string; statutTon: string } | null;
}

export interface CoordonneesFormateur {
  siret: string | null;
  siretValide: boolean;
  raisonSociale: string | null;
  adresse: string | null;
  codePostal: string | null;
  ville: string | null;
  iban: string | null;
  ibanMasque: string | null;
  bic: string | null;
  titulaireCompte: string | null;
  modifieesLe: string | null;
}

export interface FicheFormateur {
  id: string;
  utilisateurId: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string | null;
  actif: boolean;
  statut: StatutFormateur;
  statutLibelle: string;
  sites: { code: string; nom: string }[];
  tauxHoraire: number | null;
  coordonnees: CoordonneesFormateur;
  pieces: PieceFormateur[];
  piecesNonConformes: string[];
  conventions: ConventionFormateur[];
  refusConvention: string | null;
  factures: FactureFormateur[];
  fichesFacturables: { id: string; titre: string; date: string | null; siteCode: string | null; heures: number }[];
  refusTransmission: string | null;
  activite: { heuresAnimees: number; fichesValidees: number };
  peutVerifier: boolean;
}

export interface LigneFormateur {
  id: string;
  nom: string;
  prenom: string;
  statut: StatutFormateur;
  statutLibelle: string;
  sites: string[];
  actif: boolean;
  piecesAVerifier: number;
  facturesAVerifier: number;
  piecesNonConformes: number;
}

export interface ReponseFormateurs {
  formateurs: LigneFormateur[];
  sansFiche: { utilisateurId: string; nom: string; prenom: string; email: string; sites: string[] }[];
  compteurs: { formateurs: number; piecesAVerifier: number; facturesAVerifier: number };
  peutVerifier: boolean;
}

// ---------------------------------------------------------------------------
// Adresses des fichiers — routes gardées, consultations tracées
// ---------------------------------------------------------------------------

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

export function urlPieceFormateur(documentId: string): string {
  return `${BASE}/formateurs/documents/${encodeURIComponent(documentId)}/fichier`;
}

export function urlConventionFormateur(conventionId: string): string {
  return `${BASE}/formateurs/conventions/${encodeURIComponent(conventionId)}/fichier`;
}

export function urlFactureFormateur(factureId: string): string {
  return `${BASE}/formateurs/factures/${encodeURIComponent(factureId)}/fichier`;
}

/** Message d'un refus de l'API : le détail de la validation quand il y en a un. */
export function messageRefus(erreur: unknown, defaut: string): string {
  const details = detailsRefus(erreur);
  if (details.length > 0) return details.map((detail) => detail.message).join(' ');
  return erreur instanceof Error && erreur.message ? erreur.message : defaut;
}

// ---------------------------------------------------------------------------
// Formats d'affichage
// ---------------------------------------------------------------------------

const EUROS = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
const NOMBRE = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });

export function euros(montant: number): string {
  return EUROS.format(montant);
}

export function heures(valeur: number): string {
  return `${NOMBRE.format(valeur)} h`;
}

/** « 2026-10-15 » → « 15/10/2026 ». */
export function jourCourt(iso: string | null): string {
  if (!iso) return '—';
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

/** Instant ISO affiché en date réunionnaise. */
export function dateReunion(instant: string | null): string {
  if (!instant) return '—';
  return new Date(instant).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

// ---------------------------------------------------------------------------
// Espace du formateur
// ---------------------------------------------------------------------------

const CLE_MOI = ['formateurs', 'moi'] as const;

function enregistrerFiche(client: QueryClient, fiche: FicheFormateur): void {
  client.setQueryData(CLE_MOI, { fiche });
}

export function useMaFicheFormateur() {
  return useQuery({
    queryKey: CLE_MOI,
    queryFn: () => appelApi<{ fiche: FicheFormateur | null }>('/formateurs/moi'),
  });
}

export interface SaisieCoordonnees {
  siret: string;
  raisonSociale: string;
  adresse: string;
  codePostal: string;
  ville: string;
  iban: string;
  bic: string;
  titulaireCompte: string;
}

export function useEnregistrerCoordonnees() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: SaisieCoordonnees) =>
      appelApi<{ fiche: FicheFormateur }>('/formateurs/moi/coordonnees', { methode: 'PUT', corps }),
    onSuccess: ({ fiche }) => enregistrerFiche(client, fiche),
  });
}

export function useDeposerPieceFormateur() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entree: {
      type: TypePieceFormateur;
      fichier: File;
      delivreLe?: string;
      valableJusquAu?: string;
    }) => {
      const formulaire = new FormData();
      formulaire.append('type', entree.type);
      if (entree.delivreLe) formulaire.append('delivreLe', entree.delivreLe);
      if (entree.valableJusquAu) formulaire.append('valableJusquAu', entree.valableJusquAu);
      formulaire.append('fichier', entree.fichier);
      return appelApi<{ fiche: FicheFormateur }>('/formateurs/moi/documents', {
        methode: 'POST',
        formulaire,
      });
    },
    onSuccess: ({ fiche }) => enregistrerFiche(client, fiche),
  });
}

export function useRetirerPieceFormateur() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      appelApi<{ fiche: FicheFormateur }>(`/formateurs/moi/documents/${encodeURIComponent(documentId)}`, {
        methode: 'DELETE',
      }),
    onSuccess: ({ fiche }) => enregistrerFiche(client, fiche),
  });
}

export function useGenererMaConvention() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: { debut: string; fin: string }) =>
      appelApi<{ fiche: FicheFormateur }>('/formateurs/moi/conventions', { methode: 'POST', corps }),
    onSuccess: ({ fiche }) => enregistrerFiche(client, fiche),
  });
}

export interface SaisieFacture {
  numero: string;
  debut: string;
  fin: string;
  montant: string;
  site: string;
  fichier: File | null;
}

function formulaireFacture(saisie: SaisieFacture): FormData {
  const formulaire = new FormData();
  formulaire.append('numero', saisie.numero);
  formulaire.append('debut', saisie.debut);
  formulaire.append('fin', saisie.fin);
  formulaire.append('montant', saisie.montant);
  if (saisie.site) formulaire.append('site', saisie.site);
  if (saisie.fichier) formulaire.append('fichier', saisie.fichier);
  return formulaire;
}

export function useTransmettreFacture() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (saisie: SaisieFacture) =>
      appelApi<{ fiche: FicheFormateur }>('/formateurs/moi/factures', {
        methode: 'POST',
        formulaire: formulaireFacture(saisie),
      }),
    onSuccess: ({ fiche }) => enregistrerFiche(client, fiche),
  });
}

export function useCorrigerFacture() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ factureId, saisie }: { factureId: string; saisie: SaisieFacture }) =>
      appelApi<{ fiche: FicheFormateur }>(`/formateurs/moi/factures/${encodeURIComponent(factureId)}`, {
        methode: 'PUT',
        formulaire: formulaireFacture(saisie),
      }),
    onSuccess: ({ fiche }) => enregistrerFiche(client, fiche),
  });
}

// ---------------------------------------------------------------------------
// Vérification (écran « Formateurs »)
// ---------------------------------------------------------------------------

function parametresSite(site: string): string {
  return site && site !== 'TOUS' ? `?site=${encodeURIComponent(site)}` : '';
}

export function useFormateurs(site: string) {
  const parametres = parametresSite(site);
  return useQuery({
    queryKey: ['formateurs', 'liste', parametres],
    queryFn: () => appelApi<ReponseFormateurs>(`/formateurs${parametres}`),
  });
}

export function useFicheFormateur(formateurId: string | null) {
  return useQuery({
    queryKey: ['formateurs', 'fiche', formateurId],
    queryFn: () =>
      appelApi<{ fiche: FicheFormateur }>(`/formateurs/${encodeURIComponent(formateurId ?? '')}`),
    enabled: formateurId !== null,
  });
}

export interface VuePrevisionPole extends VuePrevision {
  formateur: string;
  /** Lignes d'une antenne hors du périmètre du lecteur, non affichées. */
  lignesNonAffichees: number;
}

/** Heures prévisionnelles saisies par le formateur (TF-419), en lecture. Sans mois : le mois à venir. */
export function usePrevisionFormateur(formateurId: string, mois: string | null) {
  return useQuery({
    queryKey: ['formateurs', 'heures-previsionnelles', formateurId, mois ?? 'a-venir'],
    queryFn: () =>
      appelApi<VuePrevisionPole>(
        `/formateurs/${encodeURIComponent(formateurId)}/heures-previsionnelles${
          mois ? `?mois=${encodeURIComponent(mois)}` : ''
        }`,
      ),
  });
}

/** Après un geste du pôle : la fiche rendue fait foi, la liste se recompte. */
function apresVerification(client: QueryClient, fiche: FicheFormateur): Promise<unknown> {
  client.setQueryData(['formateurs', 'fiche', fiche.id], { fiche });
  return client.invalidateQueries({ queryKey: ['formateurs', 'liste'] });
}

export function useVerifierPieceFormateur() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentId,
      decision,
      motif,
    }: {
      documentId: string;
      decision: 'CONFORME' | 'REFUSE';
      motif?: string;
    }) =>
      appelApi<{ fiche: FicheFormateur }>(
        `/formateurs/documents/${encodeURIComponent(documentId)}/verification`,
        { methode: 'PATCH', corps: { decision, motif: motif ?? null } },
      ),
    onSuccess: ({ fiche }) => apresVerification(client, fiche),
  });
}

export function useVerifierFacture() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      factureId,
      decision,
      motif,
    }: {
      factureId: string;
      decision: 'VERIFIEE' | 'A_CORRIGER';
      motif?: string;
    }) =>
      appelApi<{ fiche: FicheFormateur }>(
        `/formateurs/factures/${encodeURIComponent(factureId)}/verification`,
        { methode: 'PATCH', corps: { decision, motif: motif ?? null } },
      ),
    onSuccess: ({ fiche }) => apresVerification(client, fiche),
  });
}

export function useReglerFormateur() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      formateurId,
      statut,
      tauxHoraire,
    }: {
      formateurId: string;
      statut: StatutFormateur;
      tauxHoraire: string;
    }) =>
      appelApi<{ fiche: FicheFormateur }>(`/formateurs/${encodeURIComponent(formateurId)}`, {
        methode: 'PATCH',
        corps: { statut, tauxHoraire: tauxHoraire.trim() === '' ? null : tauxHoraire },
      }),
    onSuccess: ({ fiche }) => apresVerification(client, fiche),
  });
}

export function useGenererConventionPole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      formateurId,
      ...corps
    }: {
      formateurId: string;
      debut: string;
      fin: string;
      formationCodes: string[];
      heuresPrevues: string;
    }) =>
      appelApi<{ fiche: FicheFormateur }>(`/formateurs/${encodeURIComponent(formateurId)}/conventions`, {
        methode: 'POST',
        corps: {
          debut: corps.debut,
          fin: corps.fin,
          ...(corps.formationCodes.length > 0 ? { formationCodes: corps.formationCodes } : {}),
          heuresPrevues: corps.heuresPrevues.trim() === '' ? null : corps.heuresPrevues,
        },
      }),
    onSuccess: ({ fiche }) => apresVerification(client, fiche),
  });
}

export function useCreerFicheFormateur() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: { utilisateurId: string; statut: StatutFormateur }) =>
      appelApi<{ fiche: FicheFormateur }>('/formateurs/fiches', { methode: 'POST', corps }),
    onSuccess: ({ fiche }) => apresVerification(client, fiche),
  });
}

export interface VueDeclarationPole extends VueDeclaration {
  formateur: string;
  /** Lignes d'une antenne hors du périmètre du lecteur, non affichées. */
  lignesNonAffichees: number;
}

/** Heures réalisées déclarées par le formateur (TF-420), en lecture. Sans mois : le plus ancien à déclarer. */
export function useDeclarationFormateur(formateurId: string, mois: string | null) {
  return useQuery({
    queryKey: ['formateurs', 'heures-realisees', formateurId, mois ?? 'a-declarer'],
    queryFn: () =>
      appelApi<VueDeclarationPole>(
        `/formateurs/${encodeURIComponent(formateurId)}/heures-realisees${
          mois ? `?mois=${encodeURIComponent(mois)}` : ''
        }`,
      ),
  });
}
